import { CheckIcon } from "@heroicons/react/24/outline";
import { ASSET_CLASS_OPTIONS } from "../profileMapping";
import { CARD, CARD_ACTIVE, SECTION_LABEL } from "../ui/theme";
import type { AssetClassId, OnboardingAnswers } from "../types";

interface StepProfileProps {
  answers: OnboardingAnswers;
  onChange: (answers: OnboardingAnswers) => void;
}

/**
 * Step 1: what this trader cares about. Asset-class cards are pre-checked
 * from squawk channel roles; the free text is the concierge's input and is
 * kept verbatim.
 */
export default function StepProfile({ answers, onChange }: StepProfileProps) {
  const toggleAssetClass = (id: AssetClassId) => {
    const current = answers.assetClasses;
    onChange({
      ...answers,
      assetClasses: current.includes(id)
        ? current.filter((a) => a !== id)
        : [...current, id],
    });
  };

  return (
    <div className="space-y-7">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {ASSET_CLASS_OPTIONS.map((option, index) => {
          const selected = answers.assetClasses.includes(option.id);
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => toggleAssetClass(option.id)}
              aria-pressed={selected}
              style={{ ["--nq-i" as string]: index }}
              className={`nq-onb-rise relative px-3.5 py-3 text-left transition-all duration-200 active:scale-[0.97] cursor-pointer ${
                selected ? CARD_ACTIVE : `${CARD} hover:border-white/20`
              }`}
            >
              {selected && (
                <CheckIcon
                  className="absolute right-2.5 top-2.5 h-3.5 w-3.5 text-squawk-blue"
                  strokeWidth={3}
                />
              )}
              <div
                className={`text-sm font-semibold ${
                  selected ? "text-squawk-blue" : "text-gray-100"
                }`}
              >
                {option.label}
              </div>
              <div className="mt-0.5 text-[11px] leading-snug text-gray-500">
                {option.blurb}
              </div>
            </button>
          );
        })}
      </div>

      {/* The concierge's input — the highest-value field on the screen. */}
      <div>
        <label htmlFor="nq-onb-freetext" className={SECTION_LABEL}>
          In your words <span className="text-gray-600">— optional</span>
        </label>
        <div className="group relative mt-2">
          <textarea
            id="nq-onb-freetext"
            value={answers.freeText ?? ""}
            onChange={(e) => onChange({ ...answers, freeText: e.target.value })}
            placeholder="e.g. US tech earnings, FOMC speakers, and anything moving crude"
            rows={3}
            maxLength={2000}
            className="w-full resize-none rounded-2xl border border-white/[0.07] bg-white/[0.03] px-4 py-3 text-sm leading-relaxed text-gray-100 placeholder:text-gray-600 transition-all duration-300 focus:border-squawk-blue/50 focus:bg-white/[0.05] focus:shadow-[0_0_24px_-8px_rgba(0,174,239,0.5)] focus:outline-none"
          />
        </div>
        <p className="mt-1.5 text-[11px] text-gray-500">
          Tell us and we&apos;ll build the watchlists, filters and layout for you.
        </p>
      </div>

      <div>
        <span className={SECTION_LABEL}>Used Newsquawk before?</span>
        <div className="mt-2 flex gap-2">
          {(
            [
              { value: "new_to_squawk", label: "I'm new" },
              { value: "used_before", label: "I've used it before" },
            ] as const
          ).map((option) => {
            const selected = answers.experience === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() =>
                  onChange({
                    ...answers,
                    experience: selected ? undefined : option.value,
                  })
                }
                className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-all duration-200 active:scale-[0.97] cursor-pointer ${
                  selected
                    ? "border-squawk-blue/40 bg-squawk-blue/[0.1] text-squawk-blue"
                    : "border-white/[0.07] bg-white/[0.02] text-gray-400 hover:border-white/20 hover:text-gray-200"
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
